import {
  DatePickerField,
  SubmissionForm,
  TextInputField,
  TimePickerField,
  Heading,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { BpMobileNotification } from "@libs/api/gateways/bp-mobile/bp-mobile.dtos";

import {
  getValidNotificationDateAndTimeMessage,
  parseNotification
} from "./notification-form.utils";
import { Validator } from "@bps/validator";

export interface NotificationFormValues {
  outageMessage?: string;
  notificationTimeStartUtc?: string;
  notificationTimeEndUtc?: string;
  notificationDateStartUtc?: Date;
  notificationDateEndUtc?: Date;
}

interface NotificationFormProps {
  notification: BpMobileNotification;
  onSubmit: (values: NotificationFormValues) => Promise<void>;
}

const notificationValidator = new Validator<NotificationFormValues>(
  validator => ({
    outageMessage: validator
      .string()
      .maxLength(255, { message: "Maximum length is 255 characters." })
      .required(),
    notificationTimeStartUtc: validator.string().required(),
    notificationTimeEndUtc: validator.string().required(),
    notificationDateStartUtc: validator.date().required(),
    notificationDateEndUtc: validator.date().required()
  })
);

export const NotificationForm: React.FC<NotificationFormProps> = ({
  notification,
  onSubmit
}) => {
  const theme = useTheme();

  return (
    <SubmissionForm<NotificationFormValues>
      onSubmit={onSubmit}
      initialValues={parseNotification(notification)}
      hideCancelButton
      validate={notificationValidator.validate}
    >
      {({
        values: {
          notificationDateStartUtc,
          notificationDateEndUtc,
          notificationTimeStartUtc,
          notificationTimeEndUtc
        }
      }) => {
        const validationError = getValidNotificationDateAndTimeMessage({
          notificationStartDate: notificationDateStartUtc,
          notificationEndDate: notificationDateEndUtc,
          notificationStartTime: notificationTimeStartUtc,
          notificationEndTime: notificationTimeEndUtc
        });

        return (
          <Stack tokens={{ childrenGap: theme.spacing.m }}>
            <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
              <TextInputField
                name="outageMessage"
                label="Message"
                multiline
                styles={{ field: { height: "150px" } }}
              />

              <Heading labelPaddings>Notification window (UTC Time)</Heading>
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 4 }}
              >
                <DatePickerField name="notificationDateStartUtc" />
                <Text>to</Text>
                <DatePickerField name="notificationDateEndUtc" />
              </Stack>
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 4 }}
              >
                <TimePickerField
                  name="notificationTimeStartUtc"
                  styles={{
                    subComponentStyles: { textField: { root: { width: 112 } } }
                  }}
                />
                <Text>to</Text>
                <TimePickerField
                  name="notificationTimeEndUtc"
                  styles={{
                    subComponentStyles: { textField: { root: { width: 112 } } }
                  }}
                />
              </Stack>
            </Stack>
            {validationError && (
              <MessageBar messageBarType={MessageBarType.error}>
                {validationError}
              </MessageBar>
            )}
          </Stack>
        );
      }}
    </SubmissionForm>
  );
};
